import Stack from '@mui/material/Stack'
import MenuItem from '@mui/material/MenuItem'
import {
  CommunityDocument,
  CompetitionEntryCreateCommand,
  JoinRestriction
} from '@rallycry/api-suite-typescript/dist/models'
import { v4 as uuidv4 } from 'uuid'
import { Form, Formik, FormikHelpers } from 'formik'
import { Select, TextField } from 'formik-mui'
import * as Yup from 'yup'
import { FormikUserCommunityPicker } from '../../../../molecules/input/FormikUserCommunityPicker'
import { FormikUserSearch } from '../../../../molecules/input/FormikUserSearch'
import { AcceptableSubmit } from '../../../../molecules/input/AcceptableSubmit'
import { useCompetitionParticipant } from '@/entity/competition/useCompetitionParticipant'
import { useCompetition } from '@/entity/competition/useCompetition'
import { useApiError } from '@/core/hooks/useApiError'
import { ModalConfiguration } from '@/components/organisms/modal/ModalConfiguration'
import { LabeledField } from '@/components/organisms/form/LabeledField'
import { RcTrans } from '@/components/atoms/RcTrans'
import { useFeatures } from '@/components/providers/site/FeatureProvider'
import { FormikOrgCommunityPicker } from '@/components/molecules/input/FormikOrgCommunityPicker'
import { RcFileInput } from '@/components/molecules/input/RcFileInput'

type FormType = CompetitionEntryCreateCommand

export const TeamAdminCreate = ({
  shouldNavigate,
  onComplete
}: {
  shouldNavigate?: boolean
  onComplete: () => any
}) => {
  const { featHideCommunity } = useFeatures()
  const { organization, competition, settings } = useCompetition()
  const { handle } = useApiError()
  const { createTeam } = useCompetitionParticipant()

  const handleSubmit = async (
    values: FormType,
    helpers: FormikHelpers<FormType>
  ) => {
    try {
      const v = {
        ...values,
        leader: {
          none: !values?.leader?.participant,
          participant: values?.leader?.participant,
          player: true
        },
        representing:
          values.representing === 0 ? undefined : values.representing
      }
      await createTeam(competition?.id!, v, shouldNavigate)
      onComplete()
    } catch (e) {
      await handle(e, { values, helpers })
    }
  }

  const validation = Yup.object().shape({
    alternateName: Yup.string().required(),
    leader: Yup.object().shape({
      participant: Yup.number()
    }),
    image: Yup.string().required(),
    joinRestriction: Yup.string().required(),
    representing: settings?.entryRepresentingOptional
      ? Yup.number()
      : Yup.number()
          .required()
          .min(1, 'Competition requires a representing community')
  })

  return (
    <Formik
      validationSchema={validation}
      initialValues={
        {
          alternateName: '',
          joinRestriction: JoinRestriction.NORMAL,
          representing: 0,
          image: organization?.image || ''
        } as FormType
      }
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue }) => (
        <Form>
          <Stack direction='column' spacing={5}>
            <RcFileInput
              source={values.image}
              onComplete={async value => setFieldValue('image', value)}
              saveAs={`team-image-${uuidv4()}`}
              aspectRatio={1}
            />
            <LabeledField
              component={TextField}
              label={<RcTrans i18nKey='competition:team.name-label' />}
              name='alternateName'
              type='text'
              variant='outlined'
              fullWidth
            />
            <FormikUserSearch
              name='leader.participant'
              label={<RcTrans i18nKey='competition:team.leader-label' />}
              allowPlaceholderCreation
            />
            {featHideCommunity ? null : values.leader?.participant ? (
              <FormikUserCommunityPicker userId={values.leader?.participant} />
            ) : (
              <FormikOrgCommunityPicker
                name='representing'
                onChanged={() => {}}
              />
            )}
          </Stack>

          <ModalConfiguration
            title={<RcTrans i18nKey='competition:team.create-team' />}
          >
            <AcceptableSubmit />
          </ModalConfiguration>
        </Form>
      )}
    </Formik>
  )
}
